import { AlertTriangle, ShieldAlert, UserRound } from "lucide-react";
import { Badge } from "@/components/ui/badge";

/* NABH §2 — Patient identification banner shown on every IPD screen.
 * Renders identity, key clinical context, and a prominent allergy warning
 * pill if allergies are on file. */
export default function PatientIdentityBanner({ ipd }) {
  if (!ipd) return null;
  const allergy = ipd.allergies;
  const hasAllergy = allergy && !allergy.no_known_allergy && (allergy.entries || []).length > 0;
  const nka = allergy && allergy.no_known_allergy;
  const allergyText = hasAllergy
    ? (allergy.entries || []).map((e) => e.substance).filter(Boolean).join(", ")
    : nka ? "NO KNOWN ALLERGY" : "ALLERGY STATUS NOT RECORDED";

  return (
    <div className="bg-white border border-slate-300 sticky top-0 z-30" data-testid="patient-identity-banner">
      <div className="flex items-center gap-3 p-3">
        <div className="w-10 h-10 bg-blue-50 text-blue-700 flex items-center justify-center rounded-full">
          <UserRound className="w-5 h-5"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-base font-semibold" data-testid="ib-name">{ipd.patient_name}</span>
            <Badge className="rounded-sm bg-blue-100 text-blue-800" data-testid="ib-uhid">UHID {ipd.uhid}</Badge>
            <Badge className="rounded-sm bg-slate-100 text-slate-700" data-testid="ib-ipdno">IPD {ipd.ipd_no}</Badge>
            <span className="text-xs text-slate-500">{ipd.patient_age || "-"}y / {ipd.patient_gender || "-"}</span>
          </div>
          <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap gap-x-4">
            <span>Bed <b data-testid="ib-bed">{ipd.bed_no || "-"}</b></span>
            <span>Ward <b>{ipd.ward || "-"}</b></span>
            <span>Doctor <b>{ipd.doctor_name || "-"}</b></span>
            <span>Dx <b>{ipd.diagnosis || ipd.provisional_diagnosis || "-"}</b></span>
          </div>
        </div>

        {/* Allergy pill — always visible so no clinical action is taken without noticing */}
        <div className={`px-3 py-2 border-l-4 text-xs font-semibold flex items-center gap-2 ${
          hasAllergy ? "border-red-600 bg-red-50 text-red-800 animate-pulse"
          : nka ? "border-emerald-600 bg-emerald-50 text-emerald-800"
          : "border-amber-500 bg-amber-50 text-amber-800"
        }`} data-testid="ib-allergy">
          {hasAllergy ? <ShieldAlert className="w-4 h-4"/> : <AlertTriangle className="w-4 h-4"/>}
          <span className="max-w-[220px] truncate" title={allergyText}>{allergyText}</span>
        </div>
      </div>
    </div>
  );
}
